/**
 * WL-K5a — Client wrappers for the Kanzlei→Mandant reverse-invite Cloud Functions.
 *
 * Thin httpsCallable bindings (same pattern as {@link ./advisorInviteApi}). All
 * hard security checks live server-side (architecture-loop-b.md §4.2); these
 * helpers only marshal input/output and normalise errors for the UI.
 *
 * There is NO FE env flag here (unlike the M11b advisor invite): the whole channel
 * is gated server-side behind Loop-B-Beta, and the UI visibility is gated with
 * `canUseClientInvite(uid, sellerData)` (M-GA-4; `GA_FLAG || isLoopBBeta`) at the
 * component level. A non-beta caller hits a
 * uniform `failed-precondition`/`permission-denied` from every callable.
 *
 * @module api/clientInviteApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/** Uniform server response for the creation callable (Anti-Enumeration). */
export interface InviteClientResult {
  status: 'invited';
}

/** Server response for the read-only preview callable. */
export interface PreviewClientInviteResult {
  kanzleiName: string | null;
}

/** Server response for the acceptance callable. */
export interface AcceptClientInviteResult {
  status: 'accepted';
  kanzleiName: string | null;
}

/** Server response for the decline callable. */
export interface DeclineClientInviteResult {
  status: 'declined';
}

/** Result of the advisor-profile upsert (+ AVV acceptance). */
export interface UpsertAdvisorProfileResult {
  status: 'ok';
  avvAccepted: boolean;
}

/** A single sent client-invite entry (Kanzlei view). Never carries a token. */
export interface SentClientInvite {
  email: string | null;
  clientName: string | null;
  status: 'invited' | 'accepted' | 'declined' | 'expired';
  invitedAtMs: number | null;
  expiresAtMs: number | null;
  /** true once a mail was delivered (2xx); false = „eingeladen, noch offen". */
  mailSent: boolean;
}

/** Response envelope from the `listMyClientInvites` callable. */
export interface ListMyClientInvitesResult {
  invites: SentClientInvite[];
}

/**
 * Creates/updates the caller's Kanzlei profile and (optionally) records the
 * Kanzlei-AVV acceptance. Required once before {@link inviteClientByEmail} works.
 *
 * @param kanzleiName the Kanzlei display name (shown to invited mandanten).
 * @param beraterNr   optional DATEV Berater number (digits, ≤7).
 * @param avvAccepted set true to record the AVV acceptance timestamp.
 * @param onboardingConfirmed set true to ALSO record the versioned §5 Kanzlei-
 *                   onboarding confirmation (B4). The §5 text is the superset of the
 *                   AVV (§6.1): one checkbox in the profile step confirms both.
 */
export async function upsertMyAdvisorProfile(
  kanzleiName: string,
  beraterNr?: string | null,
  avvAccepted?: boolean,
  onboardingConfirmed?: boolean,
): Promise<UpsertAdvisorProfileResult> {
  const callable = httpsCallable<
    {
      kanzleiName: string;
      beraterNr?: string | null;
      avvAccepted?: boolean;
      onboardingConfirmed?: boolean;
    },
    UpsertAdvisorProfileResult
  >(functions, 'upsertMyAdvisorProfile');
  try {
    const res = await callable({ kanzleiName, beraterNr, avvAccepted, onboardingConfirmed });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Records the caller's versioned §5 Kanzlei-onboarding confirmation (B4) via the
 * ENTKOPPELTE, ungated `confirmKanzleiOnboarding` callable (auth-only, NO feature/
 * Beta gate — §6.2 Deadlock-Vermeidung). Writes ONLY the caller's own confirmation
 * fields (`onboardingConfirmedAt` + `onboardingVersion`) and, when no profile exists
 * yet, seeds one with fail-closed defaults; it grants NO data access. Used by the
 * §5-Onboarding-Dialog before an advisor accepts an invite.
 *
 * @param kanzleiName optional Kanzlei name used only to seed a still-missing profile.
 */
export async function confirmKanzleiOnboarding(
  kanzleiName?: string,
): Promise<{ status: 'ok' }> {
  const callable = httpsCallable<
    { confirmed: true; kanzleiName?: string },
    { status: 'ok' }
  >(functions, 'confirmKanzleiOnboarding');
  try {
    const res = await callable({ confirmed: true, kanzleiName });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Invites a mandant by e-mail. The server ALWAYS returns `{status:'invited'}`
 * (uniform — the UI must not branch on it).
 *
 * @param inviteeEmail   mandant e-mail (server normalises lowercase+trim).
 * @param advisorConfirmed the Kanzlei's mandatory confirmation checkbox value.
 * @param clientName     optional display name of the mandant.
 * @param mandantenNr    optional DATEV Mandanten number (digits).
 */
export async function inviteClientByEmail(
  inviteeEmail: string,
  advisorConfirmed: boolean,
  clientName?: string | null,
  mandantenNr?: string | null,
): Promise<InviteClientResult> {
  const callable = httpsCallable<
    { inviteeEmail: string; advisorConfirmed: boolean; clientName?: string | null; mandantenNr?: string | null },
    InviteClientResult
  >(functions, 'inviteClientByEmail');
  try {
    const res = await callable({ inviteeEmail, advisorConfirmed, clientName, mandantenNr });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Resolves ONLY the inviting Kanzlei display name for a token (read-only). Returns
 * `{ kanzleiName: null }` for any non-resolvable case (unknown/expired token,
 * non-beta Kanzlei, or the server rejecting with failed-precondition).
 *
 * @param token the plaintext token from the `?clientInvite=` URL parameter.
 */
export async function previewClientInvite(token: string): Promise<PreviewClientInviteResult> {
  const callable = httpsCallable<{ token: string }, PreviewClientInviteResult>(
    functions,
    'previewClientInvite',
  );
  try {
    const res = await callable({ token });
    return res.data;
  } catch (err) {
    const normalized = normalizeCallableError(err);
    if (normalized.code === 'failed-precondition') {
      return { kanzleiName: null };
    }
    throw normalized;
  }
}

/**
 * Accepts a client invitation. Requires the caller to be email-verified server-side
 * (the accept panel checks `emailVerified` first). On success the caller becomes the
 * tenant owner and the Kanzlei is written as an advisor member.
 *
 * @param token the plaintext token from the `?clientInvite=` URL parameter.
 * @param consentAccepted the §3-Mandanten-Consent (B3). Optional/additiv: the server
 *                 records the consent marker in the SAME activation transaction when
 *                 `true` is sent (also with the enforcement flag off — fills the
 *                 backlog before the flip). With `KANZLEI_CONSENT_ENFORCED` armed the
 *                 server requires `true` (else `failed-precondition 'consent_required'`).
 */
export async function acceptClientInviteByToken(
  token: string,
  consentAccepted?: boolean,
): Promise<AcceptClientInviteResult> {
  const callable = httpsCallable<
    { token: string; consentAccepted?: boolean },
    AcceptClientInviteResult
  >(functions, 'acceptClientInviteByToken');
  try {
    const res = await callable({ token, consentAccepted });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Declines a client invitation terminally (and suppresses re-invites from the same
 * Kanzlei). Requires email-verified + email-match server-side.
 *
 * @param token the plaintext token from the `?clientInvite=` URL parameter.
 */
export async function declineClientInvite(token: string): Promise<DeclineClientInviteResult> {
  const callable = httpsCallable<{ token: string }, DeclineClientInviteResult>(
    functions,
    'declineClientInvite',
  );
  try {
    const res = await callable({ token });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Fetches the Kanzlei's own sent client invitations (Kanzlei-only view).
 * Returns an empty list when the server rejects with failed-precondition
 * (non-beta) so the panel stays inert without showing an error.
 */
export async function listMyClientInvites(): Promise<ListMyClientInvitesResult> {
  const callable = httpsCallable<Record<string, never>, ListMyClientInvitesResult>(
    functions,
    'listMyClientInvites',
  );
  try {
    const res = await callable({});
    return res.data;
  } catch (err) {
    const normalized = normalizeCallableError(err);
    if (normalized.code === 'failed-precondition') {
      return { invites: [] };
    }
    throw normalized;
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server message
 * and exposing the coarse function error code (e.g. `failed-precondition`).
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
